import find from 'lodash/fp/find';
import formatTypes from '../../../node_modules/@suunto/suunto-information-model/Specifications/ValuesAndFormatting/FormatTypes.json';
import unitTypes from '../../../node_modules/@suunto/suunto-information-model/Specifications/ValuesAndFormatting/UnitTypes.json';
import {
  convertMeters,
  convertTime,
  convertSpeed,
  convertPace,
  convertHeartRate,
  convertEnergy,
  convertAirPressure,
  convertTemperature,
  convertRecoveryTime,
  convertRad,
  convertWeight,
} from './formatterConverters';

function format(formatObj, value, unitSystem, style, suffixTranslator, converter, inputUnit) {
  let unitSettings = unitSystem === 'metric' ? formatObj.Metric : formatObj.Imperial;
  if (!unitSettings) {
    unitSettings = formatObj.Generic; // Not all formatters have metric/imperial variants, but a 'generic' instead.
  }

  // if Style is not defined, fall back to default style defined in SIM.
  let styleSettings = style ? unitSettings.Styles[style] : null;
  if (!styleSettings) {
    styleSettings = unitSettings.Styles[formatObj.DefaultStyle];
  }

  let formatting = find((setting) => value <= setting.Range.Max)(styleSettings);
  // if all formatting options are out of valuerange, fall back to last range defined in SIM.
  if (!formatting) {
    formatting = styleSettings[styleSettings.length - 1];
  }

  const unit = formatting.Unit ? formatting.Unit : formatting.Format;
  let formatValue = inputUnit !== unit && converter ? converter(value, unit, unitSystem) : value;
  if (formatValue === null) {
    formatValue = 0;
  }

  const decimals = typeof formatting.Decimals !== 'undefined' ? formatting.Decimals : null;

  const valueString = decimals !== null && !Number.isNaN(formatValue) ? formatValue.toFixed(decimals) : formatValue;
  const suffixString = suffixTranslator ? ` ${suffixTranslator(unitTypes[formatting.Unit])}` : '';

  const formattedString = `${valueString}${suffixString}`;

  return formattedString;
}

/* Distance formatters */
export function formatDistance(value, unitSystem = 'metric', style, suffixTranslator, inputUnit) {
  const formatObj = formatTypes.Distance;
  const converter = convertMeters;

  return format(formatObj, value, unitSystem, style, suffixTranslator, converter, inputUnit);
}

export function formatSwimDistance(value, unitSystem = 'metric', style, suffixTranslator, inputUnit) {
  const formatObj = formatTypes.SwimDistance;
  const converter = convertMeters;

  return format(formatObj, value, unitSystem, style, suffixTranslator, converter, inputUnit);
}

/* Duration formatters */
export function formatDuration(value, unitSystem = 'metric', style) {
  const formatObj = formatTypes.Duration;
  const converter = convertTime;
  const suffixTranslator = false; // suffixTranslator is never used with Duration.

  return format(formatObj, value, unitSystem, style, suffixTranslator, converter);
}

export function formatDurationMs(value, unitSystem = 'metric', style) {
  const formatObj = formatTypes.DurationMs;
  const converter = convertTime;
  const suffixTranslator = false;
  const formatValue = value * 0.001; // convertTime takes seconds as parameter.

  return format(formatObj, formatValue, unitSystem, style, suffixTranslator, converter);
}

export function formatTimeOfDay(value, unitSystem = 'metric', style) {
  const formatObj = formatTypes.TimeOfDay;
  const converter = convertTime;
  const suffixTranslator = false;

  return format(formatObj, value, unitSystem, style, suffixTranslator, converter);
}

/* Speed formatters */
export function formatSpeed(value, unitSystem = 'metric', style, suffixTranslator, inputUnit) {
  const formatObj = formatTypes.Speed;
  const converter = convertSpeed;

  return format(formatObj, value, unitSystem, style, suffixTranslator, converter, inputUnit);
}

export function formatNauticalSpeed(value, unitSystem = 'metric', style, suffixTranslator, inputUnit) {
  const formatObj = formatTypes.NauticalSpeed;
  const converter = convertSpeed;

  return format(formatObj, value, unitSystem, style, suffixTranslator, converter, inputUnit);
}

export function formatNauticalDistance(value, unitSystem = 'metric', style, suffixTranslator, inputUnit) {
  const formatObj = formatTypes.NauticalDistance;
  const converter = convertMeters;

  return format(formatObj, value, unitSystem, style, suffixTranslator, converter, inputUnit);
}

export function formatVerticalSpeed(value, unitSystem = 'metric', style, suffixTranslator, inputUnit) {
  const formatObj = formatTypes.VerticalSpeed;
  const converter = convertSpeed;

  return format(formatObj, value, unitSystem, style, suffixTranslator, converter, inputUnit);
}

export function formatPace(value, unitSystem = 'metric', style, suffixTranslator, inputUnit) {
  const formatObj = formatTypes.Pace;
  const converter = convertPace;

  return format(formatObj, value, unitSystem, style, suffixTranslator, converter, inputUnit);
}

export function formatSwimPace(value, unitSystem = 'metric', style, suffixTranslator, inputUnit) {
  const formatObj = formatTypes.SwimPace;
  const converter = convertPace;

  return format(formatObj, value, unitSystem, style, suffixTranslator, converter, inputUnit);
}

/* Power formatters */
export function formatPower(value, unitSystem = 'metric', style, suffixTranslator, inputUnit) {
  const formatObj = formatTypes.Power;
  const converter = null;

  return format(formatObj, value, unitSystem, style, suffixTranslator, converter, inputUnit);
}

/* Heart rate formatters */
export function formatHeartRate(value, unitSystem = 'metric', style, suffixTranslator, inputUnit) {
  const formatObj = formatTypes.HeartRate;
  const converter = convertHeartRate;

  return format(formatObj, value, unitSystem, style, suffixTranslator, converter, inputUnit);
}

/* Energy formatters */
export function formatEnergy(value, unitSystem = 'metric', style, suffixTranslator, inputUnit) {
  const formatObj = formatTypes.Energy;
  const converter = convertEnergy;

  return format(formatObj, value, unitSystem, style, suffixTranslator, converter, inputUnit);
}

/* Training formatters */
export function formatTrainingEffect(value, unitSystem = 'metric', style) {
  const formatObj = formatTypes.TrainingEffect;
  const suffixTranslator = false;
  const converter = null;

  return format(formatObj, value, unitSystem, style, suffixTranslator, converter);
}

export function formatPeakTrainingEffect(value, unitSystem = 'metric', style) {
  const formatObj = formatTypes.PeakTrainingEffect;
  const suffixTranslator = false;
  const converter = null;

  return format(formatObj, value, unitSystem, style, suffixTranslator, converter);
}

export function formatEPOC(value, unitSystem = 'metric', style, suffixTranslator, inputUnit) {
  const formatObj = formatTypes.EPOC;
  const converter = null;

  return format(formatObj, value, unitSystem, style, suffixTranslator, converter, inputUnit);
}

export function formatVO2(value, unitSystem = 'metric', style, suffixTranslator, inputUnit) {
  const formatObj = formatTypes.VO2;
  const converter = null;

  return format(formatObj, value, unitSystem, style, suffixTranslator, converter, inputUnit);
}

export function formatCadence(value, unitSystem = 'metric', style, suffixTranslator, inputUnit) {
  const formatObj = formatTypes.Cadence;
  const converter = convertHeartRate;

  return format(formatObj, value, unitSystem, style, suffixTranslator, converter, inputUnit);
}

export function formatPerformance(value, unitSystem = 'metric', style) {
  const formatObj = formatTypes.Performance;
  const converter = null;
  const suffixTranslator = false;

  return format(formatObj, value, unitSystem, style, suffixTranslator, converter);
}

export function formatRecoveryTime(value, unitSystem = 'metric', style, suffixTranslator, inputUnit) {
  const formatObj = formatTypes.RecoveryTime;
  const converter = convertRecoveryTime;

  return format(formatObj, value, unitSystem, style, suffixTranslator, converter, inputUnit);
}

/* Altitude formatters */
export function formatAltitude(value, unitSystem = 'metric', style, suffixTranslator, inputUnit) {
  const formatObj = formatTypes.Altitude;
  const converter = convertMeters;

  return format(formatObj, value, unitSystem, style, suffixTranslator, converter, inputUnit);
}

export function formatAscent(value, unitSystem = 'metric', style, suffixTranslator, inputUnit) {
  const formatObj = formatTypes.Ascent;
  const converter = convertMeters;

  return format(formatObj, value, unitSystem, style, suffixTranslator, converter, inputUnit);
}

export function formatDescent(value, unitSystem = 'metric', style, suffixTranslator, inputUnit) {
  const formatObj = formatTypes.Descent;
  const converter = convertMeters;

  return format(formatObj, value, unitSystem, style, suffixTranslator, converter, inputUnit);
}

/* Air / temperature formatters */
export function formatAirPressure(value, unitSystem = 'metric', style, suffixTranslator, inputUnit) {
  const formatObj = formatTypes.AirPressure;
  const converter = convertAirPressure;

  return format(formatObj, value, unitSystem, style, suffixTranslator, converter, inputUnit);
}

export function formatTemperature(value, unitSystem = 'metric', style, suffixTranslator, inputUnit) {
  const formatObj = formatTypes.Temperature;
  const converter = convertTemperature;

  return format(formatObj, value, unitSystem, style, suffixTranslator, converter, inputUnit);
}

export function formatCount(value, unitSystem = 'metric', style) {
  const formatObj = formatTypes.Count;
  const suffixTranslator = false;
  const converter = null;

  return format(formatObj, value, unitSystem, style, suffixTranslator, converter);
}

export function formatStrokes(value, unitSystem = 'metric', style) {
  const formatObj = formatTypes.Strokes;
  const suffixTranslator = false;
  const converter = null;

  return format(formatObj, value, unitSystem, style, suffixTranslator, converter);
}

export function formatStrokeRate(value, unitSystem = 'metric', style, suffixTranslator, inputUnit) {
  const formatObj = formatTypes.StrokeRate;
  const converter = convertHeartRate;

  return format(formatObj, value, unitSystem, style, suffixTranslator, converter, inputUnit);
}

export function formatSwolf(value, unitSystem = 'metric', style) {
  const formatObj = formatTypes.Swolf;
  const suffixTranslator = false;
  const converter = null;

  return format(formatObj, value, unitSystem, style, suffixTranslator, converter);
}

export function formatPercentage(value, unitSystem = 'metric', style, suffixTranslator, inputUnit) {
  const formatObj = formatTypes.Percentage;
  const converter = null;

  return format(formatObj, value, unitSystem, style, suffixTranslator, converter, inputUnit);
}

/* Compass formatters */
export function formatCompassHeadingDeg(value, unitSystem = 'metric', style, suffixTranslator, inputUnit) {
  const formatObj = formatTypes.CompassHeadingDeg;
  const converter = convertRad;

  return format(formatObj, value, unitSystem, style, suffixTranslator, converter, inputUnit);
}

export function formatCompassHeadingMil(value, unitSystem = 'metric', style, suffixTranslator, inputUnit) {
  const formatObj = formatTypes.CompassHeadingMil;
  const converter = convertRad;

  return format(formatObj, value, unitSystem, style, suffixTranslator, converter, inputUnit);
}

export function formatDeclination(value, unitSystem = 'metric', style, suffixTranslator, inputUnit) {
  const formatObj = formatTypes.Declination;
  const converter = convertRad;

  return format(formatObj, value, unitSystem, style, suffixTranslator, converter, inputUnit);
}

export function formatRowingPace(value, unitSystem = 'metric', style, suffixTranslator, inputUnit) {
  const formatObj = formatTypes.RowingPace; // typo in SIM.
  const converter = convertPace;

  return format(formatObj, value, unitSystem, style, suffixTranslator, converter, inputUnit);
}

/* Weight formatters */
export function formatWeight(value, unitSystem = 'metric', style, suffixTranslator, inputUnit) {
  const formatObj = formatTypes.Weigth; // typo in SIM.
  const converter = convertWeight;

  return format(formatObj, value, unitSystem, style, suffixTranslator, converter, inputUnit);
}

export const formatterMap = {
  Distance: formatDistance,
  SwimDistance: formatSwimDistance,
  Duration: formatDuration,
  DurationMs: formatDurationMs,
  TimeOfDay: formatTimeOfDay,
  Speed: formatSpeed,
  NauticalSpeed: formatNauticalSpeed,
  NauticalDistance: formatNauticalDistance,
  VerticalSpeed: formatVerticalSpeed,
  Pace: formatPace,
  SwimPace: formatSwimPace,
  Power: formatPower,
  HeartRate: formatHeartRate,
  Energy: formatEnergy,
  TrainingEffect: formatTrainingEffect,
  PeakTrainingEffect: formatPeakTrainingEffect,
  EPOC: formatEPOC,
  VO2: formatVO2,
  Cadence: formatCadence,
  Performance: formatPerformance,
  RecoveryTime: formatRecoveryTime,
  Altitude: formatAltitude,
  Ascent: formatAscent,
  Descent: formatDescent,
  AirPressure: formatAirPressure,
  Temperature: formatTemperature,
  Count: formatCount,
  Strokes: formatStrokes,
  StrokeRate: formatStrokeRate,
  Swolf: formatSwolf,
  Percentage: formatPercentage,
  CompassHeadingDeg: formatCompassHeadingDeg,
  CompassHeadingMil: formatCompassHeadingMil,
  Declination: formatDeclination,
  Weigth: formatWeight,
  RowingPace: formatRowingPace,
};
export default formatterMap;
